import React, { useEffect, useRef, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import ProfileAvatar from '../../components/account/ProfileAvatar';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import * as auth from '../../services/auth';
import { ApiError } from '../../services/types';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * Personal information, as a page of the account rather than a sheet over it.
 *
 * This replaces `EditProfileSheet` on the customer path. The sheet rose over
 * the account list with the list showing through the scrim, which tested as
 * confusing - the page behind looked like where you were, and dismissing the
 * sheet appeared to "take you back to profile" at random. A page slides in
 * like every other account page, and Back lands exactly where it was opened
 * from, because the opener never unmounts underneath it.
 *
 * The save logic is the sheet's, moved: the screen owns `PATCH /auth/me`
 * itself (via `services/auth`) and only reports through `onSave` once the
 * server has agreed, so the caller only ever sees a finished result.
 *
 * The number is shown but not editable. It is the identity the OTP verified,
 * so changing it is a re-verification, not a text edit.
 */

/** The only field that must be filled. */
const NAME_MIN = 2;

/**
 * The two optional profile questions, as chip rows.
 *
 * ---------------------------------------------------------------------------
 * ALL OF THIS IS OPTIONAL, AND THAT IS A DESIGN DECISION
 * ---------------------------------------------------------------------------
 * None of it is needed to book a service. It is asked because it changes what
 * a visit is - a family's deep clean and one person's are different jobs - and
 * because a Pro's own comfort about which homes they are sent to is a real
 * operational constraint.
 *
 * That does not make it required. Nothing here gates a booking, nothing is
 * pre-selected, and every question carries a way to decline that is stored as
 * an answer rather than as silence. `prefer_not_to_say` exists precisely so
 * somebody who has declined is not asked again next week.
 *
 * Gender is asked ONLY when the household is a bachelor. That is the one case
 * where it is operationally used, and asking a family for it would be
 * collecting personal data with no purpose behind it.
 */
const HOUSEHOLD_OPTIONS: Array<{ key: auth.HouseholdType; label: string }> = [
  { key: 'family', label: 'Family' },
  { key: 'bachelor', label: 'Bachelor' },
];

const GENDER_OPTIONS: Array<{ key: auth.Gender; label: string }> = [
  { key: 'male', label: 'Male' },
  { key: 'female', label: 'Female' },
  { key: 'other', label: 'Other' },
  { key: 'prefer_not_to_say', label: 'Prefer not to say' },
];

/*
 * `RELATIONSHIP_OPTIONS` was here. The question is no longer asked - see the
 * note where it used to render.
 */

type Props = {
  name: string | null;
  email: string | null;
  /** Verified digits. Displayed read-only. */
  phone: string | null;
  /** Local URI of the current picture, or null. */
  photo?: string | null;
  /** Current answers, or null/undefined where nothing has been chosen yet. */
  householdType?: auth.HouseholdType | null;
  gender?: auth.Gender | null;
  onBack: () => void;
  /**
   * Called with what the server confirmed, never the raw draft.
   *
   * All four values come from the response. The two optional answers used
   * to be echoed from the draft, because the backend accepted name and email
   * only and would otherwise have wiped a selection the customer had just
   * made. That stopped being true when `PATCH /auth/me` learned to store
   * them, so this reads what was actually saved.
   */
  onSave: (next: {
    name: string;
    email: string;
    householdType: auth.HouseholdType | null;
    gender: auth.Gender | null;
  }) => void;
  /** Opens a photo picker. Omitted while no picker is installed. */
  onPickPhoto?: () => void;
};

function PersonalInfoScreen({
  name,
  email,
  phone,
  photo,
  householdType,
  gender,
  onBack,
  onSave,
  onPickPhoto,
}: Props) {
  const resp = useResponsive();

  const [draftName, setDraftName] = useState(name ?? '');
  const [draftEmail, setDraftEmail] = useState(email ?? '');
  /* Null is "not answered". Absent and null collapse to the same thing - no
     chip selected - which is what keeps an unanswered profile from sending
     anything. */
  const [draftHousehold, setDraftHousehold] =
    useState<auth.HouseholdType | null>(householdType ?? null);
  const [draftGender, setDraftGender] = useState<auth.Gender | null>(
    gender ?? null,
  );
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  /** The last failure from the server, or null. */
  const [error, setError] = useState<string | null>(null);

  /** False once the screen unmounts, so a landed request cannot set state. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const trimmed = draftName.trim();
  const nameOk = trimmed.length >= NAME_MIN;
  /* Only after the field has been left alone once - flagging an empty form
     the instant it opens tells someone off for nothing. */
  const showError = touched && !nameOk;

  /**
   * Choosing a household, and clearing a gender that no longer applies.
   *
   * Switching from bachelor to family must drop the gender answer, not hide
   * it: leaving it in state would keep sending a value the customer can no
   * longer see or change, which is the quiet kind of data collection this
   * screen is written to avoid.
   *
   * Tapping the selected chip again clears the answer. Every question here is
   * optional and there is no other way to take an answer back once given.
   */
  const pickHousehold = (next: auth.HouseholdType) => {
    const value = draftHousehold === next ? null : next;
    setDraftHousehold(value);
    if (value !== 'bachelor') {
      setDraftGender(null);
    }
  };

  const save = async () => {
    if (!nameOk || busy) {
      return;
    }
    setBusy(true);
    setError(null);

    try {
      /*
       * Both answers are spread in only when there is one to send.
       *
       * `PATCH /auth/me` accepts them now - it did not when this screen was
       * written, and sending them anyway is what produced "property
       * householdType should not exist": the DTO validates with
       * `forbidNonWhitelisted`, so one unknown key failed the whole request
       * and took the customer's NAME and EMAIL edits down with it.
       *
       * Still spread rather than always sent, because omitted and null mean
       * different things to the server: omitted leaves an answer alone, null
       * clears it. An unanswered profile has nothing to say either way.
       *
       * Gender rides on the household answer - cleared to null whenever the
       * household is not `bachelor` (see `pickHousehold`), so a family never
       * carries a stale gender from an earlier answer. The server applies the
       * same rule, so a client that forgot could not store one either.
       */
      const user = await auth.updateProfile({
        name: trimmed,
        email: draftEmail.trim(),
        ...(draftHousehold ? { householdType: draftHousehold } : {}),
        ...(draftHousehold === 'bachelor' && draftGender
          ? { gender: draftGender }
          : {}),
      });
      if (!alive.current) {
        return;
      }
      /*
       * From the RESPONSE now, not the draft.
       *
       * The note here used to say the backend did not persist these, and that
       * this should switch to `user.*` once it did. It does: `PATCH /auth/me`
       * accepts all three and answers with what it stored. Echoing the draft
       * would show the customer what they typed rather than what was saved, so
       * anything the server dropped or normalised would look right until the
       * next cold launch.
       */
      onSave({
        name: user.name ?? '',
        email: user.email ?? '',
        householdType: user.householdType ?? null,
        gender: user.gender ?? null,
      });
    } catch (thrown) {
      if (!alive.current) {
        return;
      }
      setError(
        thrown instanceof ApiError
          ? thrown.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      if (alive.current) {
        setBusy(false);
      }
    }
  };

  return (
    <View style={styles.root}>
      <PageHeader title="Personal information" onBack={onBack} />

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.body, resp.page]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ---------------------------------------------------------- photo */}
        <View style={styles.avatarWrap}>
          {/* Live, not a preview of what will be saved: the picture is stored
              the moment it is chosen, so leaving this page does not put it
              back. It is the one field here that is not a draft. */}
          <ProfileAvatar photo={photo} name={draftName} size={84} />

          {onPickPhoto ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={photo ? 'Change photo' : 'Add photo'}
              onPress={onPickPhoto}
              style={({ pressed }) => [
                styles.camera,
                pressed ? styles.pressed : null,
              ]}
            >
              <Icon
                name="plus"
                size={14}
                color={colors.onPrimary}
                strokeWidth={2.4}
              />
            </Pressable>
          ) : null}
        </View>

        {/* --------------------------------------------------------- fields */}
        <View style={styles.field}>
          <Text variant="caption" color={colors.inkMuted}>
            Full name
            <Text variant="caption" color={colors.danger}>
              {' '}
              *
            </Text>
          </Text>
          <TextInput
            value={draftName}
            onChangeText={setDraftName}
            onBlur={() => setTouched(true)}
            placeholder="Your name"
            placeholderTextColor={colors.inkSubtle}
            style={[styles.input, showError ? styles.inputError : null]}
            accessibilityLabel="Full name"
            autoCapitalize="words"
            returnKeyType="next"
          />
          {showError ? (
            <Text variant="caption" color={colors.danger}>
              Please enter your name.
            </Text>
          ) : null}
        </View>

        <View style={styles.field}>
          <Text variant="caption" color={colors.inkMuted}>
            Email
          </Text>
          <TextInput
            value={draftEmail}
            onChangeText={setDraftEmail}
            placeholder="you@example.com"
            placeholderTextColor={colors.inkSubtle}
            style={styles.input}
            accessibilityLabel="Email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        <View style={styles.field}>
          <Text variant="caption" color={colors.inkMuted}>
            Mobile number
          </Text>
          {/* Readable, not typeable - and the reason stated underneath rather
              than left to be discovered. */}
          <View style={[styles.input, styles.locked]}>
            <Text variant="body" color={colors.inkBody}>
              {phone ? auth.formatPhone(phone) : 'Not set'}
            </Text>
            <View style={styles.verified}>
              <Icon
                name="check"
                size={12}
                color={colors.success}
                strokeWidth={2.6}
              />
              <Text variant="caption" color={colors.success}>
                Verified
              </Text>
            </View>
          </View>
          <Text variant="caption" color={colors.inkMuted}>
            Changing this needs a new verification code.
          </Text>
        </View>

        {/* ------------------------------------------------------ household */}
        {/*
          No "About your home" heading and no explanatory line above this.

          Both are gone on request. The field label now carries the whole
          question - "Household type" says what is being asked without a
          heading repeating it and a sentence underneath explaining it, which
          on a short form was three pieces of chrome around two chips.

          It is still optional: nothing is preselected, tapping a chosen chip
          clears it, and no booking is gated on an answer.
        */}
        <View style={styles.field}>
          <Text variant="caption" color={colors.inkMuted}>
            Household type
          </Text>
          <View style={styles.chips}>
            {HOUSEHOLD_OPTIONS.map(option => {
              const on = draftHousehold === option.key;
              return (
                <Pressable
                  key={option.key}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: on }}
                  accessibilityLabel={option.label}
                  onPress={() => pickHousehold(option.key)}
                  style={[styles.chip, on ? styles.chipOn : null]}
                >
                  <Text
                    variant="labelSm"
                    color={on ? colors.onPrimary : colors.inkBody}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>

        {/*
          Gender, and ONLY for a bachelor household.

          Not hidden-but-present: the answer is cleared when the household
          changes (see `pickHousehold`), so nothing is retained for a customer
          who cannot see the question. Asking a family for it would be
          collecting personal data with nothing on the other end of it.
        */}
        {draftHousehold === 'bachelor' ? (
          <View style={styles.field}>
            <Text variant="caption" color={colors.inkMuted}>
              Gender
            </Text>
            <View style={styles.chips}>
              {GENDER_OPTIONS.map(option => {
                const on = draftGender === option.key;
                return (
                  <Pressable
                    key={option.key}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: on }}
                    accessibilityLabel={option.label}
                    onPress={() =>
                      setDraftGender(on ? null : option.key)
                    }
                    style={[styles.chip, on ? styles.chipOn : null]}
                  >
                    <Text
                      variant="labelSm"
                      color={on ? colors.onPrimary : colors.inkBody}
                    >
                      {option.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ) : null}

        {/*
          Relationship status was asked here, and is now gone from the wire
          as well as from the screen.

          It was never used operationally - household type is what changes
          the visit, and gender is what a Pro-matching preference would
          read. Marital status answered neither, so it was a personal
          question with nothing on the other end of it.

          It was still being SENT after the field was removed, to preserve an
          answer given before then. That stopped being possible: the server
          no longer accepts the field, and its validation refuses an unknown
          property outright rather than ignoring it - so continuing to send
          it would fail the whole save, name and email included. The stored
          answer is simply left alone now, which is what preserving it meant
          in the first place.
        */}

        {error ? (
          <View style={styles.error} accessibilityRole="alert">
            <Icon name="xCircle" size={14} color={colors.danger} />
            <Text variant="caption" color={colors.danger} style={styles.errorText}>
              {error}
            </Text>
          </View>
        ) : null}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Save changes"
          accessibilityState={{ disabled: !nameOk || busy }}
          disabled={!nameOk || busy}
          onPress={save}
          style={({ pressed }) => [
            styles.save,
            nameOk && !busy ? null : styles.saveOff,
            pressed && nameOk && !busy ? styles.pressed : null,
          ]}
        >
          <Text variant="label" color={colors.onPrimary}>
            {busy ? 'Saving…' : 'Save changes'}
          </Text>
        </Pressable>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  body: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xl,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
    gap: spacing.lg,
  },
  avatarWrap: {
    alignSelf: 'center',
  },
  camera: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.ink,
    borderWidth: 2,
    borderColor: colors.background,
  },
  field: {
    gap: spacing.xs,
  },
  /* Wraps, because "Prefer not to say" is long and four options do not fit on
     one line on a small phone. */
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  chip: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  chipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  input: {
    ...inputText,
    minHeight: 50,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  inputError: {
    borderColor: colors.danger,
  },
  locked: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surfaceMuted,
  },
  verified: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  error: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
  },
  errorText: {
    flex: 1,
  },
  save: {
    height: 52,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.md,
    backgroundColor: colors.primary,
  },
  saveOff: {
    backgroundColor: colors.borderStrong,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default PersonalInfoScreen;
